import type { Daylight, UiLang } from "./types";

/** MET sine symbolkoder til emoji. Dekker alle familiene i Locationforecast. */
const SYMBOLS: [RegExp, string][] = [
  [/^clearsky_night/, "🌙"],
  [/^clearsky/, "☀️"],
  [/^fair_night/, "🌛"],
  [/^fair/, "🌤️"],
  [/^partlycloudy/, "⛅"],
  [/^cloudy/, "☁️"],
  [/^fog/, "🌫️"],
  [/thunder/, "⛈️"],
  [/sleet/, "🌨️"],
  [/snow/, "❄️"],
  [/rain/, "🌧️"],
];

export function weatherEmoji(symbol: string): string {
  for (const [pattern, emoji] of SYMBOLS) {
    if (pattern.test(symbol)) return emoji;
  }
  return "🌡️";
}

/** Gjør «partlycloudy_day» om til lesbar tekst på valgt språk. */
export function weatherLabel(symbol: string, lang: UiLang): string {
  const base = symbol.replace(/_(day|night|polartwilight)$/, "");
  const names: Record<UiLang, Record<string, string>> = {
    no: {
      clearsky: "Klarvær",
      fair: "Lettskyet",
      partlycloudy: "Delvis skyet",
      cloudy: "Overskyet",
      fog: "Tåke",
      rain: "Regn",
      lightrain: "Lett regn",
      heavyrain: "Kraftig regn",
      rainshowers: "Regnbyger",
      lightrainshowers: "Lette regnbyger",
      heavyrainshowers: "Kraftige regnbyger",
      snow: "Snø",
      lightsnow: "Lett snø",
      heavysnow: "Kraftig snø",
      snowshowers: "Snøbyger",
      sleet: "Sludd",
      lightsleet: "Lett sludd",
      heavysleet: "Kraftig sludd",
      sleetshowers: "Sluddbyger",
    },
    en: {
      clearsky: "Clear skies",
      fair: "Fair",
      partlycloudy: "Partly cloudy",
      cloudy: "Overcast",
      fog: "Fog",
      rain: "Rain",
      lightrain: "Light rain",
      heavyrain: "Heavy rain",
      rainshowers: "Rain showers",
      lightrainshowers: "Light rain showers",
      heavyrainshowers: "Heavy rain showers",
      snow: "Snow",
      lightsnow: "Light snow",
      heavysnow: "Heavy snow",
      snowshowers: "Snow showers",
      sleet: "Sleet",
      lightsleet: "Light sleet",
      heavysleet: "Heavy sleet",
      sleetshowers: "Sleet showers",
    },
    de: {
      clearsky: "Klarer Himmel",
      fair: "Leicht bewölkt",
      partlycloudy: "Teilweise bewölkt",
      cloudy: "Bedeckt",
      fog: "Nebel",
      rain: "Regen",
      lightrain: "Leichter Regen",
      heavyrain: "Starker Regen",
      rainshowers: "Regenschauer",
      lightrainshowers: "Leichte Regenschauer",
      heavyrainshowers: "Starke Regenschauer",
      snow: "Schnee",
      lightsnow: "Leichter Schneefall",
      heavysnow: "Starker Schneefall",
      snowshowers: "Schneeschauer",
      sleet: "Schneeregen",
      lightsleet: "Leichter Schneeregen",
      heavysleet: "Starker Schneeregen",
      sleetshowers: "Schneeregenschauer",
    },
    fr: {
      clearsky: "Ciel dégagé",
      fair: "Peu nuageux",
      partlycloudy: "Partiellement nuageux",
      cloudy: "Couvert",
      fog: "Brouillard",
      rain: "Pluie",
      lightrain: "Pluie faible",
      heavyrain: "Forte pluie",
      rainshowers: "Averses",
      lightrainshowers: "Averses faibles",
      heavyrainshowers: "Fortes averses",
      snow: "Neige",
      lightsnow: "Neige faible",
      heavysnow: "Fortes chutes de neige",
      snowshowers: "Averses de neige",
      sleet: "Neige fondue",
      lightsleet: "Neige fondue faible",
      heavysleet: "Forte neige fondue",
      sleetshowers: "Averses de neige fondue",
    },
    es: {
      clearsky: "Cielo despejado",
      fair: "Poco nuboso",
      partlycloudy: "Parcialmente nuboso",
      cloudy: "Cubierto",
      fog: "Niebla",
      rain: "Lluvia",
      lightrain: "Lluvia débil",
      heavyrain: "Lluvia fuerte",
      rainshowers: "Chubascos",
      lightrainshowers: "Chubascos débiles",
      heavyrainshowers: "Chubascos fuertes",
      snow: "Nieve",
      lightsnow: "Nevada débil",
      heavysnow: "Nevada fuerte",
      snowshowers: "Chubascos de nieve",
      sleet: "Aguanieve",
      lightsleet: "Aguanieve débil",
      heavysleet: "Aguanieve fuerte",
      sleetshowers: "Chubascos de aguanieve",
    },
  };
  return names[lang][base] ?? base.replace(/_/g, " ");
}

/** Datoformatering via Intl, så språkene får sin egen ordstilling. */
const LOCALE: Record<UiLang, string> = {
  no: "nb-NO",
  en: "en-GB",
  de: "de-DE",
  fr: "fr-FR",
  es: "es-ES",
};

/*
 * Forkortelsen for time.
 *
 * Tysk skriver «Std.», resten «h» — og norsk «t». Dette sto som
 * `lang === "no" ? "t" : "h"` og ville gitt tyske brukere «h» uten at noe
 * klaget; en tabell tvinger fram et svar for hvert språk.
 */
const HOUR: Record<UiLang, string> = {
  no: "t",
  en: "h",
  de: "Std.",
  fr: "h",
  es: "h",
};

/** Midnattssol og mørketid — begreper som ikke finnes i alle språk. */
const MIDNIGHT_SUN: Record<UiLang, string> = {
  no: "Midnattssol",
  en: "Midnight sun",
  de: "Mitternachtssonne",
  fr: "Soleil de minuit",
  es: "Sol de medianoche",
};

const POLAR_NIGHT: Record<UiLang, string> = {
  no: "Mørketid",
  en: "Polar night",
  de: "Polarnacht",
  fr: "Nuit polaire",
  es: "Noche polar",
};

export function formatDate(iso: string | null, lang: UiLang): string {
  if (!iso) return "";
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString(LOCALE[lang], {
    weekday: "long",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

export function formatDateShort(iso: string | null, lang: UiLang): string {
  if (!iso) return "";
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString(LOCALE[lang], {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

/** Klokkeslett i norsk tid — reisen skjer der, uansett brukerens språk. */
export function formatTime(iso: string | null, lang: UiLang): string {
  if (!iso) return "–";
  return new Date(iso).toLocaleTimeString(LOCALE[lang], {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Oslo",
  });
}

export function formatDuration(minutes: number, lang: UiLang): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const h = HOUR[lang];
  if (hours === 0) return `${rest} min`;
  return rest === 0 ? `${hours} ${h}` : `${hours} ${h} ${rest} min`;
}

export function formatHours(hours: number, lang: UiLang): string {
  const h = HOUR[lang];
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  const whole = Math.floor(hours);
  const minutes = Math.round((hours - whole) * 60);
  return minutes === 0 ? `${whole} ${h}` : `${whole} ${h} ${minutes} min`;
}

export function daylightSummary(daylight: Daylight, lang: UiLang): string {
  if (daylight.midnightSun) return MIDNIGHT_SUN[lang];
  if (daylight.polarNight) return POLAR_NIGHT[lang];
  return `${formatTime(daylight.sunrise, lang)}–${formatTime(daylight.sunset, lang)}`;
}

/** Farge for utsiktsindeksen, fra rust (dårlig) via sand til mose (bra). */
export function scoreColor(score: number): string {
  if (score >= 75) return "#4F7A5B";
  if (score >= 55) return "#7E9A6B";
  if (score >= 40) return "#C79A3C";
  return "#C1521F";
}
